/**
 * OpenHousePage.jsx — Open House prospecting planner (/openhouse)
 *
 * Weekend agent-prospecting circuit: browse scraped open houses (Redfin +
 * Village Properties), build a time-window-aware day route, run it in the
 * field, then work the follow-up queue. Playbook tab holds the field strategy.
 *
 * Tab state lives in ?tab= so Sidebar flyouts can deep-link.
 */
import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import { useMobile } from '../hooks/useMobile';
import { useFocus } from '../hooks/useFocus';
import { useOhPlans } from '../hooks/useOpenHouses';
import BrowseTab from '../components/openhouse/BrowseTab';
import PlanTab from '../components/openhouse/PlanTab';
import RunMode from '../components/openhouse/RunMode';
import FollowUpTab from '../components/openhouse/FollowUpTab';
import PlaybookTab from '../components/openhouse/PlaybookTab';

const TAB_IDS = ['browse', 'plan', 'run', 'followup', 'playbook'];

export default function OpenHousePage() {
  const { isMobile } = useMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab = TAB_IDS.includes(tabParam) ? tabParam : 'browse';
  // Selected plan is shared across Browse (add-to-plan), Plan, and Run tabs
  const [planId, setPlanId] = useState(null);
  const { isHidden } = useFocus();
  const { data: plans = [] } = useOhPlans();
  const runHidden = isHidden('openhouse.runFollowup') && !plans.some((p) => p.status && p.status !== 'draft');

  const setTab = useCallback((t) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', t);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const openPlan = useCallback((id) => {
    if (id) setPlanId(id);
    setTab('plan');
  }, [setTab]);

  const runPlan = useCallback((id) => {
    if (id) setPlanId(id);
    setTab('run');
  }, [setTab]);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700 }}>
          Open Houses
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        variant={isMobile ? 'scrollable' : 'standard'}
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
      >
        <Tab value="browse" label="Browse" sx={{ minHeight: 40 }} />
        <Tab value="plan" label="Plan" sx={{ minHeight: 40 }} />
        {(!runHidden || tab === 'run') && <Tab value="run" label="Run" data-focus="openhouse.runFollowup" sx={{ minHeight: 40 }} />}
        {(!runHidden || tab === 'followup') && <Tab value="followup" label="Follow-up" data-focus="openhouse.runFollowup" sx={{ minHeight: 40 }} />}
        <Tab value="playbook" label="Playbook" sx={{ minHeight: 40 }} />
      </Tabs>

      {tab === 'browse' && <BrowseTab selectedPlanId={planId} onOpenPlan={openPlan} />}
      {tab === 'plan' && <PlanTab planId={planId} onSelectPlan={setPlanId} onRun={runPlan} />}
      {tab === 'run' && <RunMode planId={planId} onSelectPlan={setPlanId} onExit={() => openPlan(planId)} />}
      {tab === 'followup' && <FollowUpTab />}
      {tab === 'playbook' && <PlaybookTab />}
    </Box>
  );
}
